import { inject, Injectable } from '@angular/core';

import { map, mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import {
  BarVppApplicationConfigurationService,
  BrandVppApplicationConfigurationService,
  FrontDeskVppApplicationConfigurationService,
  HousekeepingVppApplicationConfigurationService,
  KioskVppApplicationConfigurationService,
  LocationVppApplicationConfigurationService,
  RoomVppApplicationConfigurationService,
  StaffVppApplicationConfigurationService,
} from '@era/settings-sdk';
import { EnvironmentService } from '@methylium/core';
import { EMPTY, Observable } from 'rxjs';
import { compareVppApplications, safeMap } from '../effects.helpers';
import { CategoryType } from '../shared/types';
import {
  CategoryVppApplicationsApiActions,
  CategoryVppApplicationsEffectActions,
  CategoryVppApplicationsResolverActions,
  CategoryVppApplicationsViewActions,
  DeviceConfiguredApplicationsResolverActions,
} from './category-vpp-applications.actions';
import { VppApplicationConfiguration, vppApplicationConfigurationFrom } from './category-vpp-applications.state';

@Injectable()
export class CategoryVppApplicationsEffects {
  protected features = inject(EnvironmentService).environment.features;

  constructor(
    private readonly actions: Actions,
    private readonly brandVppApplicationService: BrandVppApplicationConfigurationService,
    private readonly locationVppApplicationService: LocationVppApplicationConfigurationService,
    private readonly roomVppApplicationService: RoomVppApplicationConfigurationService,
    private readonly barVppApplicationService: BarVppApplicationConfigurationService,
    private readonly frontDeskVppApplicationService: FrontDeskVppApplicationConfigurationService,
    private readonly housekeepingVppApplicationService: HousekeepingVppApplicationConfigurationService,
    private readonly kioskVppApplicationService: KioskVppApplicationConfigurationService,
    private readonly staffVppApplicationService: StaffVppApplicationConfigurationService
  ) {}

  /**
   * The category vpp application configuration has been updated.
   * Effect: we submit the update to the api and dispatch an error if applicable.
   */
  categoryVppApplicationLinkUpdatedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryVppApplicationsViewActions.applicationConfigurationsUpdated),
      mergeMap((action) =>
        safeMap(
          this.categoryVppApplicationLinkUpdatedEffectBuilder(action),
          () =>
            CategoryVppApplicationsEffectActions.applicationsRequested({
              categoryId: action.categoryId,
              categoryType: action.categoryType,
            }),
          (error) =>
            CategoryVppApplicationsApiActions.applicationConfigurationsUpdatedError({
              categoryId: action.categoryId,
              categoryType: action.categoryType,
              error,
            })
        )
      )
    );
  });

  /**
   * The (re)load of the category vpp applications has been requested.
   * Effect: we load the state from the api.
   */
  categoryVppApplicationsRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        CategoryVppApplicationsResolverActions.applicationsRequested,
        DeviceConfiguredApplicationsResolverActions.vPPApplicationsRequested,
        CategoryVppApplicationsEffectActions.applicationsRequested
      ),
      mergeMap((action) =>
        safeMap(
          this.categoryVppApplicationsRequestedEffectBuilder(action),
          (categoryVppApplications) =>
            CategoryVppApplicationsApiActions.applicationsLoaded({
              applications: categoryVppApplications,
              categoryType: action.categoryType,
              categoryId: action.categoryId,
            }),
          (error) =>
            CategoryVppApplicationsApiActions.applicationsLoadedError({
              error,
            })
        )
      )
    );
  });

  /**
   * The vpp applications couldn't be updated.
   * Effect: we request a reload of the state from the api.
   */
  categoryVppApplicationsUpdatedErrorEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryVppApplicationsApiActions.applicationConfigurationsUpdatedError),
      map((action) =>
        CategoryVppApplicationsEffectActions.applicationsRequested({
          categoryId: action.categoryId,
          categoryType: action.categoryType,
        })
      )
    );
  });

  private categoryVppApplicationLinkUpdatedEffectBuilder(
    action: ReturnType<typeof CategoryVppApplicationsViewActions.applicationConfigurationsUpdated>
  ): Observable<unknown> {
    switch (action.categoryType) {
      case 'Brand':
        return this.brandVppApplicationService.updateAllVppApplicationsForBrand(
          action.categoryId,
          compareVppApplications(action)
        );
      case 'Location':
        return this.locationVppApplicationService.updateAllVppApplicationsForLocation(
          action.categoryId,
          compareVppApplications(action)
        );
      case 'Room':
        return this.roomVppApplicationService.updateAllVppApplicationsForRoom(
          action.categoryId,
          compareVppApplications(action)
        );
      case 'Bar':
        return this.barVppApplicationService.updateAllVppApplicationsForBar(
          action.categoryId,
          compareVppApplications(action)
        );
      case 'FrontDesk':
        return this.frontDeskVppApplicationService.updateAllVppApplicationsForFrontDesk(
          action.categoryId,
          compareVppApplications(action)
        );
      case 'Housekeeping':
        return this.housekeepingVppApplicationService.updateAllVppApplicationsForHousekeeping(
          action.categoryId,
          compareVppApplications(action)
        );
      case 'Kiosk':
        return this.kioskVppApplicationService.updateAllVppApplicationsForKiosk(
          action.categoryId,
          compareVppApplications(action)
        );
      case 'Staff':
        return this.staffVppApplicationService.updateAllVppApplicationsForStaff(
          action.categoryId,
          compareVppApplications(action)
        );
      case 'None':
      default:
        // Return empty observable for None or unknown types
        return EMPTY;
    }
  }

  private categoryVppApplicationsRequestedEffectBuilder(action: {
    categoryId: string;
    categoryType: CategoryType;
  }): Observable<VppApplicationConfiguration[]> {
    switch (action.categoryType) {
      case 'Brand':
        return this.brandVppApplicationService
          .getAllVppApplicationsForBrand(action.categoryId)
          .pipe(map((configurations) => configurations.map(vppApplicationConfigurationFrom)));
      case 'Location':
        return this.locationVppApplicationService
          .getAllVppApplicationsForLocation(action.categoryId)
          .pipe(map((configurations) => configurations.map(vppApplicationConfigurationFrom)));
      case 'Room':
        return this.roomVppApplicationService
          .getAllVppApplicationsForRoom(action.categoryId)
          .pipe(map((configurations) => configurations.map(vppApplicationConfigurationFrom)));
      case 'Bar':
        return this.barVppApplicationService
          .getAllVppApplicationsForBar(action.categoryId)
          .pipe(map((configurations) => configurations.map(vppApplicationConfigurationFrom)));
      case 'FrontDesk':
        return this.frontDeskVppApplicationService
          .getAllVppApplicationsForFrontDesk(action.categoryId)
          .pipe(map((configurations) => configurations.map(vppApplicationConfigurationFrom)));
      case 'Housekeeping':
        return this.housekeepingVppApplicationService
          .getAllVppApplicationsForHousekeeping(action.categoryId)
          .pipe(map((configurations) => configurations.map(vppApplicationConfigurationFrom)));
      case 'Kiosk':
        return this.kioskVppApplicationService
          .getAllVppApplicationsForKiosk(action.categoryId)
          .pipe(map((configurations) => configurations.map(vppApplicationConfigurationFrom)));
      case 'Staff':
        return this.staffVppApplicationService
          .getAllVppApplicationsForStaff(action.categoryId)
          .pipe(map((configurations) => configurations.map(vppApplicationConfigurationFrom)));
      case 'None':
      default:
        // Return empty observable for None or unknown types
        return EMPTY;
    }
  }
}
